import { PrivacyPolicy, TermsOfUse } from '@strata/react-page-templates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { ReferralRedirect } from './referrals/ReferralRedirect';

import { AuthCallback } from 'components/auth/AuthCallback';
import { LoggedOut } from 'components/auth/LoggedOut';
import { RequireAuth } from 'components/auth/RequireAuth';
import { BatchesAndSchedulingPage } from 'components/batchesAndScheduling/BatchesAndSchedulingPage';
import { Home } from 'components/home/Home';
import { UpdatesHistoryScreen } from 'components/home/UpdatesHistoryScreen';
import { AllMessages } from 'components/messages/AllMessages';
import { ConversationRedirect } from 'components/messages/ConversationRedirect';
import { OpportunitiesListPage } from 'components/opportunities/OpportunitiesListPage';
import { OpportunityDetailPage } from 'components/opportunities/OpportunityDetailPage';
import { ReferralDetailPage } from 'components/referrals/ReferralDetailPage';
import { ReferralsListPage } from 'components/referrals/ReferralsListPage';
import { RequestsListPage } from 'components/requests/RequestsListPage';
import { ResolvedRequestsListPage } from 'components/requests/ResolvedRequestsListPage';
import { BulkAttachAdHocValueCase } from 'components/tools/BulkAttachAdHocValueCase';
import { BulkTransfer } from 'components/tools/BulkTransfer';
import { BulkTransition } from 'components/tools/BulkTransition';
import { BulkUnmanage } from 'components/tools/BulkUnmanage';
import { BulkUpdateReferralExpiration } from 'components/tools/BulkUpdateReferralExpiration';
import { CloseClaims } from 'components/tools/CloseClaims';
import { OrtfProcessingPage } from 'components/tools/ortfProcessing/OrtfProcessingPage';
import { PriorAuthorization } from 'components/tools/priorAuthorization/PriorAuthorization';
import { TestReferral } from 'components/tools/testReferral/TestReferral';
import { TreatmentMergeCaseDetailsPage } from 'components/tools/treatmentMergeCases/TreatmentMergeCaseDetailsPage';
import { TreatmentMergeCasesListPage } from 'components/tools/treatmentMergeCases/TreatmentMergeCasesListPage';
import { TreatmentDetailPage } from 'components/treatments/TreatmentDetailPage';
import { TreatmentSearch } from 'components/treatments/TreatmentSearch';
import { UpcomingTreatmentsListPage } from 'components/upcomingTreatments/UpcomingTreatmentsListPage';
import { AppRoutes } from 'services/routes';

export const App = () => {
    const { showTreatmentMergeQueue = false } = useFlags();

    return (
        <BrowserRouter basename="/">
            <Routes>
                <Route path={AppRoutes.callback} element={<AuthCallback />} />
                <Route path={AppRoutes.loggedOut} element={<LoggedOut />} />
                <Route element={<RequireAuth />}>
                    <Route path={AppRoutes.home} element={<Home />} />
                    <Route path={AppRoutes.referralsList} element={<ReferralsListPage />} />
                    <Route path={AppRoutes.referralRedirect()} element={<ReferralRedirect />} />
                    <Route path={AppRoutes.referralDetails()} element={<ReferralDetailPage />} />
                    <Route path={AppRoutes.opportunitiesList} element={<OpportunitiesListPage />} />
                    <Route
                        path={AppRoutes.opportunityDetails()}
                        element={<OpportunityDetailPage />}
                    />
                    <Route path={AppRoutes.allMessages} element={<AllMessages />} />
                    <Route
                        path={AppRoutes.conversationDetails()}
                        element={<ConversationRedirect />}
                    />
                    <Route
                        path={AppRoutes.requests}
                        element={<Navigate replace to={AppRoutes.requestsList} />}
                    />
                    <Route path={AppRoutes.requestsList} element={<RequestsListPage />} />
                    <Route
                        path={AppRoutes.requestsResolved}
                        element={<ResolvedRequestsListPage />}
                    />
                    <Route path={AppRoutes.treatmentSearch} element={<TreatmentSearch />} />
                    <Route path={AppRoutes.treatmentDetails()} element={<TreatmentDetailPage />} />
                    <Route path={AppRoutes.priorAuthorization} element={<PriorAuthorization />} />
                    <Route path={AppRoutes.bulkTransfer} element={<BulkTransfer />} />
                    <Route path={AppRoutes.bulkTransition} element={<BulkTransition />} />
                    <Route path={AppRoutes.bulkUnmanage} element={<BulkUnmanage />} />
                    <Route
                        path={AppRoutes.bulkAdHocValueCase}
                        element={<BulkAttachAdHocValueCase />}
                    />
                    <Route
                        path={AppRoutes.bulkUpdateReferralExpiration}
                        element={<BulkUpdateReferralExpiration />}
                    />
                    <Route path={AppRoutes.closeClaims} element={<CloseClaims />} />
                    {showTreatmentMergeQueue && (
                        <>
                            <Route
                                path={AppRoutes.treatmentMergeCases}
                                element={<TreatmentMergeCasesListPage />}
                            />
                            <Route
                                path={AppRoutes.payerTreatmentMergeCases()}
                                element={<TreatmentMergeCasesListPage />}
                            />
                            <Route
                                path={AppRoutes.treatmentMergeCaseDetails()}
                                element={<TreatmentMergeCaseDetailsPage />}
                            />
                        </>
                    )}
                    <Route path={AppRoutes.ortfProcessing} element={<OrtfProcessingPage />} />
                    <Route path={AppRoutes.testReferral} element={<TestReferral />} />
                    <Route
                        path={AppRoutes.upcomingTreatments}
                        element={<UpcomingTreatmentsListPage />}
                    />
                    <Route
                        path={AppRoutes.batchesAndScheduling}
                        element={<BatchesAndSchedulingPage />}
                    />
                    <Route path={AppRoutes.termsOfUse} element={<TermsOfUse />} />
                    <Route path={AppRoutes.privacyPolicy} element={<PrivacyPolicy />} />
                    <Route path={AppRoutes.updatesHistory} element={<UpdatesHistoryScreen />} />
                </Route>
                <Route path="*" element={<Navigate replace to={AppRoutes.home} />} />
            </Routes>
        </BrowserRouter>
    );
};
